import { useId, useState, type CSSProperties } from 'react'

export type RangeSliderProps = {
  label: string
  hint?: string
  min?: number
  max?: number
  step?: number
  /** The value. Pass it to control the slider from the parent. */
  value?: number
  /** The starting value when the slider keeps its own. The midpoint by default. */
  defaultValue?: number
  onChange?: (value: number) => void
  /** Writes a value for people, such as "25 miles". Used for the output, the end labels and aria-valuetext. */
  format?: (value: number) => string
  disabled?: boolean
  id?: string
}

/**
 * A native range input with its label, the current value in an output element
 * and the ends of the range written beside the track. Arrows step the value,
 * Page Up and Page Down move in larger steps, Home and End go to the ends: all
 * from the browser. aria-valuetext reads the value in words, with its unit.
 */
export default function RangeSlider({
  label,
  hint,
  min = 0,
  max = 100,
  step = 1,
  value,
  defaultValue = Math.round((min + max) / 2),
  onChange,
  format = String,
  disabled = false,
  id,
}: RangeSliderProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = `${fieldId}-hint`
  const [own, setOwn] = useState(defaultValue)
  const current = value ?? own
  const fill = max > min ? ((current - min) / (max - min)) * 100 : 0

  return (
    <div className="mds-field mds-range-field">
      <label className="mds-label" htmlFor={fieldId}>
        {label}
      </label>
      {hint && (
        <span className="mds-hint" id={hintId}>
          {hint}
        </span>
      )}
      <output className="mds-range__value" htmlFor={fieldId}>
        {format(current)}
      </output>
      <div className="mds-range__row">
        <span className="mds-range__end" aria-hidden="true">
          {format(min)}
        </span>
        <input
          id={fieldId}
          type="range"
          className="mds-range"
          min={min}
          max={max}
          step={step}
          value={current}
          disabled={disabled}
          aria-valuetext={format(current)}
          aria-describedby={hint ? hintId : undefined}
          style={{ '--mds-range-fill': `${fill}%` } as CSSProperties}
          onChange={(event) => {
            const next = Number(event.target.value)
            setOwn(next)
            onChange?.(next)
          }}
        />
        <span className="mds-range__end" aria-hidden="true">
          {format(max)}
        </span>
      </div>
    </div>
  )
}
